'use client';

import {
  type MarginSystemProps,
  Pagination as FreddyPagination,
  PaginationItem,
  type PaginationItemRenderProps
} from '@coolblue-development/freddy';
import selectPageNumberTrigger from '@coolblue-development/ga4-events/triggers/Pagination/selectPageNumberTrigger';
import selectPreviousOrNextTrigger from '@coolblue-development/ga4-events/triggers/Pagination/selectPreviousOrNextTrigger';
import { useAnalytics } from '@coolblue-development/next-analytics/client';
import { useFreddyTranslations } from '@coolblue-development/next-design-system-utils/client';
import { useTranslation } from '@i18n/useTranslation';
import Link from 'next/link';
import { usePathname, useSearchParams } from 'next/navigation';
import { type SyntheticEvent, useContext, useState } from 'react';

import { FilterPageContext } from '../../context';
import { useCreateQueryString } from '../../context/hooks';

export default function Pagination({
  totalPages,
  isIndexable = false,
  ...rest
}: { totalPages: number; isIndexable?: boolean } & MarginSystemProps) {
  const paginationTranslations = useFreddyTranslations('pagination');
  const { t } = useTranslation('webshop');
  const { trackEvent } = useAnalytics();

  const pageQueryParamKey = t('filterPage.page').toLowerCase();
  const searchParams = useSearchParams();
  const [activePage, setActivePage] = useState(() =>
    searchParams?.get(pageQueryParamKey) ? Number(searchParams.get(pageQueryParamKey)) : 1
  );
  const pathname = usePathname();

  const { updateCurrentPage } = useContext(FilterPageContext);
  const createQueryString = useCreateQueryString();

  if (totalPages <= 1) {
    return <></>;
  }

  function handleOnClick({
    event,
    itemType,
    itemPage
  }: {
    event: SyntheticEvent<Element, Event>;
    itemType: 'page' | 'previous' | 'next';
    itemPage: number;
  }) {
    event.stopPropagation();
    event.preventDefault();

    window?.scrollTo({ top: 0 });

    updateCurrentPage?.(itemPage, () => {
      if (itemType === 'page') {
        trackEvent(selectPageNumberTrigger({ index: itemPage + 1 }));
      } else {
        trackEvent(
          selectPreviousOrNextTrigger({
            index: itemPage,
            total_indices: totalPages,
            direction: itemType
          })
        );
      }
    });
  }

  function composeRenderedItem(item: PaginationItemRenderProps) {
    const newPath =
      pathname +
      (item.page === 1
        ? ''
        : `?${createQueryString?.(pageQueryParamKey, String(item.page)) || ''}`);

    const restProps: { rel?: string } = {};

    if (isIndexable) {
      if (item.type === 'next' && item.page <= totalPages) {
        restProps.rel = 'next';
      }

      if (item.type === 'previous' && item.page >= 1) {
        restProps.rel = 'prev';
      }
    }

    return (
      <Link href={newPath} passHref legacyBehavior>
        <PaginationItem
          component='a'
          onClick={event => handleOnClick({ event, itemType: item.type, itemPage: item.page })}
          {...restProps}
        >
          {item.label}
        </PaginationItem>
      </Link>
    );
  }

  return (
    <FreddyPagination
      translations={paginationTranslations}
      activePage={activePage}
      totalPages={totalPages}
      onChange={setActivePage}
      renderItem={composeRenderedItem}
      siblingCount={{ xs: 0, md: 1 }}
      {...rest}
    />
  );
}
